/**
 * PHARMAKON — domain-specific components
 *
 * Ported from the design's components/domain/Domain.jsx. ESM imports
 * replace the prototype's window-attached globals. The data-bound
 * components (ProductSearchCombobox, FournisseurSelector, LicenceBanner)
 * are wired to the real existing stores / API per scope answer #4.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Icon,
  Input,
  Select,
  EmptyState,
  MoneyText,
  Skeleton,
} from "@/components/ui/primitives";
import { sensEcart } from "@/lib/tresorerie/comptes.js";
import { etatEcheance } from "@/lib/tresorerie/operations.js";
import { ECHEANCE } from "@/config/tresorerie-operations.messages.js";
import { Sparkline } from "@/components/charts/Charts";
import { WORKFLOW_STATES } from "@/config/workflow-states.config.js";
import { normalizeTone, resolveStatus } from "@/config/status.config.js";
import { LOCALE_LABELS } from "@/config/locale.config.js";
import { MODULE_CATALOGUE } from "@/config/modules.config.js";
import { AUDIT_ACTIONS } from "@/config/audit-log.config.js";
import { can } from "@/config/permissions.config.js";
import { calcChifa } from "@/lib/chifa-formula.js";
import { daysUntil, lotStatus } from "@/lib/format.js";
import { fmtMoney, fmtDate, fmtTime, fmtDateLong } from "@/config/locale.config.js";
import { useAuthStore } from "@/stores/authStore.js";
import { useFournisseurStore } from "@/stores/index.js";
import { CatalogueProductPicker } from "@/components/produit/CatalogueProductPicker.jsx";

// ============================================================
// WorkflowStateChip
// ============================================================
type Tone =
  | "neutral"
  | "accent"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "theorique";
export interface WorkflowStateChipProps {
  entity: string;
  status: string;
  size?: "sm" | "md" | "lg";
}
/**
 * The WORKFLOW_STATES entity names that have a row set in the status registry
 * (config/status.config.js). The registry wins: a status keeps one word, tone
 * and icon on every screen (ux/01 §2.3, §3.10).
 */
const WORKFLOW_ENTITY_TO_STATUS_DOMAIN: Record<string, string> = {
  achat: "facture_achat",
};
export const WorkflowStateChip: React.FC<WorkflowStateChipProps> = ({
  entity,
  status,
  size = "md",
}) => {
  const domain = WORKFLOW_ENTITY_TO_STATUS_DOMAIN[entity];
  if (domain && resolveStatus(domain, status)) {
    return <Badge status={`${domain}.${status}`} size={size} />;
  }
  const def = (WORKFLOW_STATES as any)?.[entity]?.states?.[status];
  if (!def)
    return (
      <Badge tone="neutral" size={size}>
        {status}
      </Badge>
    );
  return (
    <Badge tone={normalizeTone(def.tone ?? "neutral") as Tone} size={size}>
      {def.label}
    </Badge>
  );
};

// ============================================================
// RoleBadge
// ============================================================
export interface RoleBadgeProps {
  role: string;
  size?: "sm" | "md" | "lg";
}
export const RoleBadge: React.FC<RoleBadgeProps> = ({ role, size = "md" }) => {
  const tones: Record<string, Tone> = {
    PROPRIETAIRE: "accent",
    MANAGER: "info",
    CAISSIER: "success",
    FOURNISSEUR: "warning",
    ADMIN_PHARMAKON: "danger",
    LABORATOIRE: "neutral",
  };
  return (
    <Badge tone={tones[role] || "neutral"} size={size}>
      {(LOCALE_LABELS as Record<string, string>)?.[role] || role}
    </Badge>
  );
};

// ============================================================
// PermissionGate
// ============================================================
export interface PermissionGateProps {
  need?: string; // "resource:action"
  fallback?: React.ReactNode;
  children?: React.ReactNode;
}
export const PermissionGate: React.FC<PermissionGateProps> = ({
  need,
  fallback = null,
  children,
}) => {
  const user = useAuthStore((s: any) => s.user);
  if (!need) return <>{children}</>;
  const [resource, action] = need.split(":");
  return can(user, resource, action) ? <>{children}</> : <>{fallback}</>;
};

// ============================================================
// KpiCard
// ============================================================

/**
 * Un pourcentage au format algérien francophone : « 45,9 % ».
 *
 * Exporté parce que les tuiles KPI ne sont pas seules à afficher un
 * pourcentage : un taux de marge rendu « 94.05 % » à côté d'un delta rendu
 * « 45,9 % » sur la même ligne se voit immédiatement, et c'est le genre de
 * détail qui fait douter du reste des chiffres.
 */
export const fmtPourcentFr = (v: unknown, decimales = 1): string => {
  const n = Number(v);
  if (v == null || !Number.isFinite(n)) return "—";
  return `${n.toLocaleString("fr-DZ", { maximumFractionDigits: decimales })} %`;
};
/**
 * L'ANATOMIE d'une tuile KPI (batch-1 §2 — « rendre le design des KPI vraiment
 * professionnel »). Elle est la MÊME partout — propriétaire, manager, caissier
 * — parce que c'est la répétition qui rend une grille lisible :
 *
 *   1. un libellé en capitales discrètes, précédé d'une pastille d'icône teintée ;
 *   2. LA VALEUR, grande, monospace tabulaire (les chiffres s'alignent d'une
 *      tuile à l'autre — c'est ce qui distingue un tableau de bord d'une page) ;
 *   3. le DELTA par rapport à la période précédente : ▲/▼ + pourcentage, en
 *      couleur SÉMANTIQUE (vert/rouge), jamais en couleur d'accent — et sa
 *      direction « souhaitable » est déclarée, pas devinée (voir `trendGood`) ;
 *   4. une sous-ligne de contexte, et une étincelle de tendance quand il y en a
 *      une à montrer.
 *
 * Trois règles non négociables, chacune apprise d'un écran qui a menti :
 *
 *   • `loading` rend un SQUELETTE À LA FORME DE LA TUILE, pas « 0,00 DA ». Un
 *     zéro pendant le chargement se lit comme une mauvaise journée.
 *   • un `trend` qui n'est pas un nombre fini n'affiche RIEN. `NaN %` sur un
 *     tableau de bord de gestion, c'est la fin de la confiance.
 *   • aucun chiffre en dessous de 13 px (plancher de confort 55+) : le delta
 *     monte à `text-sm`, pas `text-2xs`.
 */
export interface KpiCardProps {
  label: React.ReactNode;
  value: React.ReactNode;
  suffix?: React.ReactNode;
  sub?: React.ReactNode;
  trend?: number | null;
  /**
   * La direction SOUHAITABLE de la variation.
   * « +18 % de ruptures de stock » n'est pas une bonne nouvelle : sans cette
   * déclaration, la tuile la peindrait en vert.
   */
  trendGood?: "up" | "down" | "neutral";
  /** Ce à quoi on compare — « vs 30 j préc. ». Affiché à côté du delta. */
  trendLabel?: React.ReactNode;
  icon?: string;
  tone?: "default" | "success" | "warning" | "danger" | "accent";
  spark?: number[];
  hideMoney?: boolean;
  /** Squelette à la forme de la tuile, le temps que la mesure arrive. */
  loading?: boolean;
  /** Rend la tuile actionnable (descente vers l'écran qui explique le chiffre). */
  onClick?: () => void;
  /**
   * La tuile est un VRAI lien vers la liste filtrée (ux/01 §3.11) : focus
   * ring, Entrée, clic milieu, et un aria-label qui redit la valeur.
   */
  to?: string;
  /** Nom accessible du lien / bouton ; sinon « {label} : {valeur}, ouvrir la liste ». */
  ariaLabel?: string;
  /**
   * La base de comparaison est nulle : pas de pourcentage (« ▼100 % » serait
   * faux), un « — » et son explication.
   */
  trendBaseZero?: boolean;
  /**
   * Paire réel / théorique (D-06) : la valeur principale est le RÉEL, celle-ci
   * le THÉORIQUE (part CNAS en attente du paiement du bordereau).
   */
  theorique?: React.ReactNode;
  /** Libellés de la paire (défaut « Réel » / « Théorique »). */
  pairLabels?: [React.ReactNode, React.ReactNode];
  className?: string;
}
export const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  suffix,
  sub,
  trend,
  trendGood = "up",
  trendLabel,
  icon,
  tone = "default",
  spark,
  hideMoney,
  loading = false,
  onClick,
  to,
  ariaLabel,
  trendBaseZero = false,
  theorique,
  pairLabels = ["Réel", "Théorique"],
  className = "",
}) => {
  const navigate = useNavigate();
  const toneIconColor =
    tone === "success"
      ? "text-success"
      : tone === "warning"
        ? "text-warning"
        : tone === "danger"
          ? "text-danger"
          : tone === "accent"
            ? "text-accent"
            : "text-text-muted";
  const toneChipBg =
    tone === "success"
      ? "bg-success-soft"
      : tone === "warning"
        ? "bg-warning-soft"
        : tone === "danger"
          ? "bg-danger-soft"
          : tone === "accent"
            ? "bg-accent-soft"
            : "bg-bg-sunken";
  // ERP KPI instrument signature — the 3px tone-coloured top border.
  const toneTop =
    tone === "success"
      ? "border-t-success"
      : tone === "warning"
        ? "border-t-warning"
        : tone === "danger"
          ? "border-t-danger"
          : tone === "accent"
            ? "border-t-accent"
            : "border-t-border-strong";
  // Un delta n'est affiché QUE s'il est un nombre fini. `null` (« pas de
  // période précédente »), `undefined` et `NaN` se ressemblent beaucoup dans un
  // payload ; à l'écran ils doivent tous les trois se traduire par « rien »,
  // jamais par « NaN % » ni par « 0 % ».
  const deltaValide =
    typeof trend === "number" && Number.isFinite(trend) ? trend : null;
  // La direction souhaitable décide de la SÉMANTIQUE, pas le signe. Une hausse
  // des ruptures est rouge ; une baisse des annulations est verte.
  const favorable =
    deltaValide == null || deltaValide === 0 || trendGood === "neutral"
      ? null
      : trendGood === "down"
        ? deltaValide < 0
        : deltaValide > 0;
  const trendChipTone =
    deltaValide == null
      ? null
      : favorable === null
        ? {
            bg: "bg-bg-sunken",
            fg: "text-text-muted",
            fleche: deltaValide > 0 ? "▲" : deltaValide < 0 ? "▼" : "=",
          }
        : favorable
          ? { bg: "bg-success-soft", fg: "text-success", fleche: deltaValide > 0 ? "▲" : "▼" }
          : { bg: "bg-danger-soft", fg: "text-danger", fleche: deltaValide > 0 ? "▲" : "▼" };
  // ux/01 §3.11 — le libellé est en casse de phrase, 14 px/600, et passe à la
  // ligne plutôt que d'être tronqué (plus de micro-capitales).
  const Label = (
    <div className="flex items-start gap-2 min-w-0">
      {icon && (
        <span
          className={cn(
            "inline-flex items-center justify-center w-6 h-6 rounded-md shrink-0",
            toneChipBg,
          )}
        >
          <Icon name={icon} size={14} className={toneIconColor} />
        </span>
      )}
      <p className="pk-kpi-label">{label}</p>
    </div>
  );
  if (hideMoney) {
    return (
      <Card
        padding="md"
        className={cn(
          "pk-kpi relative transition-colors hover:border-border-default",
          className,
        )}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          {Label}
        </div>
        <div className="text-md text-text-muted italic flex items-center gap-1.5">
          <Icon name="eye-off" size={16} /> Masqué pour ce rôle
        </div>
      </Card>
    );
  }
  // ── Squelette à la FORME de la tuile ────────────────────────────────────
  // Trois blocs aux dimensions des trois blocs réels (libellé, valeur,
  // sous-ligne) : la grille ne bouge pas d'un pixel quand les chiffres
  // arrivent — et jamais « 0 » pendant le chargement (ux/01 §3.12).
  if (loading) {
    return (
      <Card
        padding="md"
        aria-busy="true"
        className={cn(
          "pk-kpi relative border-t-[3px]",
          toneTop,
          className,
        )}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0 w-full">
            {icon && <Skeleton w={24} h={24} className="rounded-md shrink-0" />}
            <Skeleton w="55%" h={14} />
          </div>
        </div>
        <Skeleton w="70%" h={30} className="mb-2" />
        <div className="min-h-[18px]">
          <Skeleton w="45%" h={14} />
        </div>
        <span className="sr-only" role="status">
          Chargement…
        </span>
      </Card>
    );
  }

  const texte = (n: React.ReactNode) =>
    typeof n === "string" || typeof n === "number" ? String(n) : "";
  const nomAccessible =
    ariaLabel ??
    (texte(label) && texte(value)
      ? `${texte(label)} : ${texte(value)}${suffix ? ` ${texte(suffix)}` : ""}, ouvrir la liste`
      : undefined);
  const actionnable = Boolean(to || onClick);
  const Conteneur: any = to ? "a" : onClick ? "button" : "div";
  const valeur = (v: React.ReactNode, theo = false) => (
    <span className={cn("pk-kpi-value", theo && "pk-kpi-value--theorique")}>
      {theo && <Icon name="hourglass" size={18} />}
      {v}
      {suffix && <span className="pk-kpi-unit">{suffix}</span>}
    </span>
  );
  return (
    <Card
      padding="md"
      style={{ containerType: "inline-size" }}
      className={cn(
        "pk-kpi relative transition-colors hover:border-border-default border-t-[3px]",
        toneTop,
        actionnable && "cursor-pointer hover:border-accent-line",
        className,
      )}
    >
      <Conteneur
        {...(to
          ? {
              href: to,
              "aria-label": nomAccessible,
              className: "pk-kpi-hit",
              onClick: (e: React.MouseEvent) => {
                // Navigation SPA sur un clic simple ; clic milieu / Ctrl+clic
                // gardent le comportement natif d'un lien.
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                onClick?.();
                navigate(to);
              },
            }
          : onClick
            ? {
                type: "button",
                onClick,
                "aria-label": nomAccessible,
                className: "pk-kpi-hit",
              }
            : {})}
      >
      <div className="flex items-start justify-between gap-2 mb-3">
        {Label}
        {trendBaseZero ? (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-sm font-semibold shrink-0 bg-bg-sunken text-text-muted"
            title="Pas de valeur sur la période de comparaison : variation non calculable."
          >
            —<span className="sr-only"> variation non calculable (base nulle)</span>
          </span>
        ) : (
          trendChipTone && (
            <span
              className={cn(
                // `text-sm` (14 px) : un CHIFFRE ne descend pas sous le
                // plancher de confort 55+, fût-il un pourcentage.
                "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-sm font-semibold shrink-0 tabular-nums",
                trendChipTone.bg,
                trendChipTone.fg,
              )}
            >
              <Icon
                name={deltaValide > 0 ? "trending-up" : deltaValide < 0 ? "trending-down" : "minus"}
                size={14}
              />
              {/* Delta signé en toutes lettres (ux/01 §3.11) : « +12 % vs 30
                  jours précédents » — virgule décimale fr-DZ. */}
              {deltaValide > 0 ? "+" : deltaValide < 0 ? "\u2212" : ""}
              {fmtPourcentFr(Math.abs(deltaValide))}
              {trendLabel && <span className="sr-only"> {trendLabel}</span>}
            </span>
          )
        )}
      </div>
      {theorique != null ? (
        <div className="pk-kpi-pair">
          <div>
            <span className="pk-kpi-pair-label">{pairLabels[0]}</span>
            {valeur(value)}
          </div>
          <div title="Part CNAS en attente du paiement du bordereau.">
            <span className="pk-kpi-pair-label">{pairLabels[1]}</span>
            {valeur(theorique, true)}
          </div>
        </div>
      ) : (
        <div className="flex items-baseline gap-1.5 mb-1">{valeur(value)}</div>
      )}
      <div className="flex items-center justify-between gap-3 min-h-[18px]">
        {/* La sous-ligne porte le CONTEXTE : ce à quoi le delta compare, la
            décomposition du chiffre, ou la légende de la paire D-06. */}
        {theorique != null && !sub ? (
          <span className="pk-kpi-sub">Part CNAS en attente du paiement du bordereau.</span>
        ) : sub || (trendChipTone && trendLabel) || trendBaseZero ? (
          <span className="pk-kpi-sub">
            {sub ?? (trendBaseZero ? "Pas de base de comparaison" : trendLabel)}
          </span>
        ) : (
          <span />
        )}
        {spark && spark.length > 1 && (
          <Sparkline
            data={spark}
            width={84}
            height={20}
            color="var(--color-accent)"
          />
        )}
      </div>
      </Conteneur>
    </Card>
  );
};

/**
 * KpiTile — the canonical name of the KPI tile (ux/01 §3.0, §3.11). One
 * implementation: KpiCard (175 call sites) IS the tile.
 */
export const KpiTile = KpiCard;

// ============================================================
// EcartText (Trésorerie E-5 — design/ux/02 §9.5, §16.2; RC-32)
// A cash écart in WORDS first: « Manque 1 200,00 DA » / « Excédent 300,00 DA » / « Conforme ».
// The tone comes from the one status registry (`ecart_caisse`): a shortage and an excess are both
// warnings with their word — never an inverted colour, never colour alone. The value is the server's
// « compté − attendu » decimal string; `classement` is the server's verdict against the officine's
// tolerance (« CONFORME » within it). An unknown écart reads « — », never « Conforme ».
// ============================================================
export interface EcartTextProps {
  value: string | null | undefined;
  classement?: string | null;
  /** Badge (default) or plain inline text (dense grid cells). */
  variant?: "badge" | "text";
  className?: string;
}
export const EcartText: React.FC<EcartTextProps> = ({ value, classement = null, variant = "badge", className }) => {
  const e = sensEcart(value, classement);
  if (!e) return <span className={cn("pk-ecart pk-ecart--inconnu", className)} aria-label="Écart inconnu">—</span>;
  const status = resolveStatus("ecart_caisse", e.cle);
  const word = status?.label ?? e.cle;
  // « Conforme » alone when exact; a tolerated écart keeps its amount next to the word.
  const amount = e.cle === "CONFORME" && /^0+(\.0+)?$/.test(e.montant) ? null : <MoneyText value={e.montant} negativeInk={false} />;
  if (variant === "text") {
    return (
      <span className={cn("pk-ecart", `pk-ecart--${normalizeTone(status?.tone)}`, className)} data-ecart={e.cle}>
        <span className="pk-ecart-mot">{word}</span>
        {amount && <> {amount}</>}
      </span>
    );
  }
  return (
    <Badge status={`ecart_caisse.${e.cle}`} className={cn("pk-ecart", className)}>
      <span data-ecart={e.cle}>
        {word}
        {amount && <> {amount}</>}
      </span>
    </Badge>
  );
};

// ============================================================
// DueChip (Trésorerie E-6, UX 02 §14.2; TWP-20) — a Brouillon's announced date against the
// officine's today, in words: « en retard de 3 j » (warning, never auto-confirmed — TD-89),
// « aujourd'hui » (info), « dans 2 jours » (neutral). Both days are « YYYY-MM-DD » in the officine's
// time zone (the caller converts with `jourOfficine`); no date → nothing.
// ============================================================
export interface DueChipProps {
  jour: string | null | undefined;
  aujourdhui: string;
  className?: string;
}
export const DueChip: React.FC<DueChipProps> = ({ jour, aujourdhui, className }) => {
  const e = etatEcheance(jour ?? null, aujourdhui);
  if (!e) return null;
  const mot = e.cle === "EN_RETARD" ? ECHEANCE.retard(e.jours) : e.cle === "AUJOURDHUI" ? ECHEANCE.aujourdhui : ECHEANCE.dans(e.jours);
  return (
    <Badge status={`echeance_operation.${e.cle}`} size="sm" className={cn("pk-due-chip", className)}>
      <span data-echeance={e.cle}>{mot}</span>
    </Badge>
  );
};
export type KpiTileProps = KpiCardProps;

// ============================================================
// StockLotBadge
// ============================================================
export interface StockLot {
  num_lot?: string;
  date_peremption?: string | null;
  quantite_disponible?: number;
  expired?: boolean;
}
export interface StockLotBadgeProps {
  lot: StockLot;
  showLot?: boolean;
}
export const StockLotBadge: React.FC<StockLotBadgeProps> = ({
  lot,
  showLot = true,
}) => {
  const status = lotStatus(lot);
  const days = daysUntil(lot.date_peremption) ?? 0;
  const tone: Tone =
    status === "expired"
      ? "danger"
      : status === "expiring-soon"
        ? "warning"
        : status === "empty"
          ? "neutral"
          : "success";
  const label =
    status === "expired"
      ? `Périmé · J${days}`
      : status === "expiring-soon"
        ? `Expire dans ${days}j`
        : status === "empty"
          ? "Vide"
          : `Sain · ${Math.round(days / 30)} mois`;
  return (
    <div className="inline-flex items-center gap-2">
      {showLot && (
        <span className="font-mono text-2xs text-text-muted">
          {lot.num_lot}
        </span>
      )}
      <Badge tone={tone} size="sm" dot>
        {label}
      </Badge>
    </div>
  );
};

// ============================================================
// ChifaFormulaCard
// ============================================================
const Row: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  mono?: boolean;
  accent?: boolean;
  strong?: boolean;
}> = ({ label, value, mono, accent, strong }) => (
  <>
    <span className="text-text-muted">{label}</span>
    <span
      className={cn(
        "text-right",
        mono && "font-mono num",
        strong
          ? "text-text-strong font-semibold"
          : accent
            ? "text-accent"
            : "text-text-default",
      )}
    >
      {value}
    </span>
  </>
);

export interface ChifaLine {
  ppa?: number;
  tr?: number;
  shp?: number;
  fae?: number;
  is_local?: boolean;
}
export interface ChifaFormulaCardProps {
  line: ChifaLine;
  taux_pec?: number;
  qte?: number;
  className?: string;
}
export const ChifaFormulaCard: React.FC<ChifaFormulaCardProps> = ({
  line,
  taux_pec = 80,
  qte = 1,
  className = "",
}) => {
  const calc = React.useMemo(
    () => calcChifa({ ...line, qte, taux_pec }),
    [line, taux_pec, qte],
  );
  return (
    <Card padding="md" className={className}>
      <CardHeader
        title="Formule CHIFA"
        subtitle={`Taux PEC ${taux_pec}% · qté ${qte}`}
      />
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Row label="PPA" value={fmtMoney(line.ppa)} mono />
        <Row label="TR (réf.)" value={fmtMoney(line.tr)} mono />
        <Row label="SHP" value={fmtMoney(line.shp)} mono />
        <Row label="FAE" value={fmtMoney(line.fae)} mono />
        <hr className="col-span-2 border-border-subtle my-1" />
        <Row
          label="Base remboursable"
          value={fmtMoney(calc.base_remboursable)}
          mono
          accent
        />
        <Row label="Part Assurance (AS)" value={fmtMoney(calc.as)} mono accent />
        <Row label="Part Patient (PS)" value={fmtMoney(calc.ps)} mono accent />
        <Row label="Total ligne" value={fmtMoney(calc.total)} mono strong />
      </div>
    </Card>
  );
};

// ============================================================
// ModuleGateCallout
// ============================================================
export interface ModuleGateCalloutProps {
  moduleCode: string;
  action?: string;
}
export const ModuleGateCallout: React.FC<ModuleGateCalloutProps> = ({
  moduleCode,
  action = "utiliser cette fonctionnalité",
}) => {
  const mod = MODULE_CATALOGUE.find((m) => m.code === moduleCode);
  if (!mod) return null;
  return (
    <Card padding="lg" className="border-2 border-dashed border-accent/30">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-accent-soft text-accent flex items-center justify-center shrink-0">
          <Icon name="lock-keyhole" size={22} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-md font-semibold text-text-strong">
              Module verrouillé
            </h3>
            <Badge tone="warning" size="sm">
              Optionnel
            </Badge>
          </div>
          <p className="text-sm text-text-default mb-1">
            Pour <span className="font-medium">{action}</span>, activez le
            module{" "}
            <span className="font-semibold text-accent">{mod.nom}</span>.
          </p>
          <p className="text-xs text-text-muted mb-3">{mod.description}</p>
          <ul className="space-y-1 mb-4">
            {mod.benefits.map((b: string) => (
              <li
                key={b}
                className="flex items-center gap-2 text-xs text-text-default"
              >
                <Icon name="check" size={12} className="text-success" /> {b}
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-3">
            <Button variant="primary" icon="zap" size="sm">
              Activer · {fmtMoney(mod.prix_mensuel)}/an
            </Button>
            <Button variant="ghost" size="sm">
              En savoir plus
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

// ============================================================
// AuditTrailTimeline
// ============================================================
export interface AuditEvent {
  id: string | number;
  action: string;
  created_at: string;
  description?: React.ReactNode;
  ip?: string;
  user?: string;
  user_role?: string;
}
export interface AuditTrailTimelineProps {
  events?: AuditEvent[];
  compact?: boolean;
}
export const AuditTrailTimeline: React.FC<AuditTrailTimelineProps> = ({
  events = [],
  compact = false,
}) => (
  <ol className="relative pl-6">
    <span className="absolute left-[7px] top-1.5 bottom-1.5 w-px bg-border-subtle"></span>
    {events.map((e) => {
      const act = (AUDIT_ACTIONS as any)[e.action] || {
        label: e.action,
        tone: "neutral",
        icon: "circle",
      };
      const toneClass =
        act.tone === "success"
          ? "text-success bg-success-soft border-success"
          : act.tone === "warning"
            ? "text-warning bg-warning-soft border-warning"
            : act.tone === "danger"
              ? "text-danger bg-danger-soft border-danger"
              : act.tone === "info"
                ? "text-info bg-info-soft border-info"
                : "text-text-muted bg-bg-sunken border-border-default";
      return (
        <li key={e.id} className={cn("relative", compact ? "pb-3" : "pb-5")}>
          <span
            className={cn(
              "absolute -left-6 top-0.5 w-[14px] h-[14px] rounded-full border-2 border-bg flex items-center justify-center",
              toneClass,
            )}
          >
            <Icon name={act.icon} size={8} />
          </span>
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-sm font-medium text-text-strong">
              {act.label}
            </span>
            <span className="text-2xs text-text-muted font-mono">
              {fmtDate(e.created_at)} · {fmtTime(e.created_at)}
            </span>
            {!compact && e.ip && (
              <span className="text-2xs text-text-muted font-mono">
                · {e.ip}
              </span>
            )}
          </div>
          <p className="text-xs text-text-default">{e.description}</p>
          {!compact && (
            <p className="text-2xs text-text-muted mt-0.5">
              par <span className="text-text-default">{e.user}</span>
              {e.user_role && (
                <span className="ml-1">
                  ({(LOCALE_LABELS as Record<string, string>)?.[e.user_role] ||
                    e.user_role}
                  )
                </span>
              )}
            </p>
          )}
        </li>
      );
    })}
  </ol>
);

// ============================================================
// ProductSearchCombobox — refonte WP-37 / cross-module XWP-12: the catalogue preset of the one
// ProductPicker (components/produit/CatalogueProductPicker.jsx) under its historical name. The
// hand-written copy (its own debounce, no request sequence, a failed search shown as « no result »)
// is gone; `onSelect` still receives the `GET /produits` row.
// ============================================================
export interface ProductHit {
  id: string | number;
  nom_commercial: string;
  dci?: string | { nom?: string };
  dosage?: string;
  code_barre?: string;
  is_remboursable?: boolean;
  ppa?: number | string;
  qte_totale?: number;
}
export interface ProductSearchComboboxProps {
  onSelect?: (p: ProductHit) => void;
  autoFocus?: boolean;
  placeholder?: string;
}
// The preset is a .jsx forwardRef (props documented in JSDoc, not typed): typed here at the seam.
const CataloguePicker = CatalogueProductPicker as unknown as React.ComponentType<
  ProductSearchComboboxProps & { onSelect?: (p: ProductHit) => void }
>;
export const ProductSearchCombobox: React.FC<ProductSearchComboboxProps> = ({ onSelect, autoFocus, placeholder }) => (
  <CataloguePicker onSelect={onSelect} autoFocus={autoFocus} {...(placeholder ? { placeholder } : {})} />
);

// ============================================================
// FournisseurSelector — reads existing useFournisseurStore dropdown
// ============================================================
export interface FournisseurSelectorProps {
  value?: string;
  onChange?: (id: string) => void;
  placeholder?: string;
  /** Only the fournisseurs connected to Pharmakon (`est_connecte`, OP-03-33) — e.g. catalogues (AUD-BC-13). */
  connectesSeulement?: boolean;
}
export const FournisseurSelector: React.FC<FournisseurSelectorProps> = ({
  value,
  onChange,
  placeholder = "Choisir un fournisseur…",
  connectesSeulement = false,
}) => {
  const dropdown = useFournisseurStore((s: any) => s.dropdown) as Array<{
    id: string;
    nom: string;
    est_connecte?: boolean;
  }>;
  // The store records the failure on `dropdownError`; surfacing it here is what
  // stops an empty picker from reading as "you have no connected suppliers"
  // when the truth is that the supplier read failed (T4 — SILENT error state on
  // /commercial/catalogue and /commercial/appel-offres, both of which gate
  // their whole page behind this picker).
  const dropdownError = useFournisseurStore((s: any) => s.dropdownError) as
    | string
    | null;
  const fetchDropdown = useFournisseurStore((s: any) => s.fetchDropdown);
  React.useEffect(() => {
    if (!dropdown || dropdown.length === 0) fetchDropdown?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdown, fetchDropdown]);
  const options = [
    { value: "", label: dropdownError ? "Liste indisponible" : placeholder },
    ...(dropdown || [])
      .filter((f) => !connectesSeulement || f.est_connecte === true)
      .map((f) => ({ value: f.id, label: f.nom })),
  ];
  return (
    <div className="space-y-1.5">
      <Select
        options={options}
        value={value || ""}
        disabled={!!dropdownError && (dropdown || []).length === 0}
        aria-invalid={dropdownError ? true : undefined}
        onChange={(e) => onChange?.((e.target as HTMLSelectElement).value)}
      />
      {dropdownError && (
        <div className="flex items-center gap-1.5 text-2xs text-danger">
          <Icon name="alert-circle" size={12} className="shrink-0" />
          <span className="flex-1 min-w-0">{dropdownError}</span>
          <button
            type="button"
            className="font-semibold underline underline-offset-2 hover:no-underline"
            onClick={() => fetchDropdown?.()}
          >
            Réessayer
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================
// LicenceBanner
// ============================================================
export const LicenceBanner: React.FC = () => {
  const user = useAuthStore((s: any) => s.user);
  const pharmacie = user?.pharmacie;
  const jours = pharmacie?.licence_jours_restants ?? 999;
  if (jours > 14) return null;
  const tone = jours <= 7 ? "danger" : "warning";
  return (
    <div
      className={cn(
        "flex items-center gap-3 px-5 py-2.5 border-b text-sm",
        tone === "danger"
          ? "bg-danger-soft border-danger/30 text-danger"
          : "bg-warning-soft border-warning/30 text-warning",
      )}
    >
      <Icon name="alert-triangle" size={16} />
      <span className="font-medium">
        {jours <= 0
          ? "Votre licence est expirée."
          : `Votre licence expire dans ${jours} jour${jours > 1 ? "s" : ""}.`}
      </span>
      <span className="text-text-muted">
        Plan {pharmacie?.licence_plan} · échéance{" "}
        {fmtDateLong(pharmacie?.licence_date_fin)}
      </span>
      <Button variant="link" size="sm" className="ml-auto">
        Renouveler maintenant →
      </Button>
    </div>
  );
};

// ============================================================
// PageHeader — the Novera Pro page header (components/ui/PageHeader.tsx,
// WP-16). Kept here under its historical import path so the ~60 pages that
// import it from Domain get the responsive header (h1 never squeezed, wrapping
// actions, self-scrolling tab strip — EXE-40) without a second component.
// ============================================================
import {
  PageHeader as UiPageHeader,
  type Crumb,
  type PageHeaderProps as UiPageHeaderProps,
} from "@/components/ui/PageHeader";

export type BreadcrumbItem = Crumb;
export type PageHeaderProps = UiPageHeaderProps;
export const PageHeader: React.FC<PageHeaderProps> = (props) => (
  <UiPageHeader {...props} />
);

// Re-export a "useUI" shim that maps the design's API onto existing infra,
// so future page ports can import { useUI } directly.
import { useThemeStore } from "@/stores/themeStore.js";
import { useLocation } from "react-router-dom";
export const useUI = () => {
  const theme = useThemeStore((s) => s.theme);
  const density = useThemeStore((s) => s.density);
  const sidebarCollapsed = useThemeStore((s) => s.sidebarCollapsed);
  const showTweaks = useThemeStore((s) => s.showTweaks);
  const themePref = useThemeStore((s) => s.themePref);
  const loc = useLocation();
  return {
    theme,
    themePref,
    density,
    sidebarCollapsed,
    showTweaks,
    page: loc.pathname.replace(/^\//, "").split("/")[0] || "dashboard",
    pageParam: loc.pathname.split("/")[2] ?? null,
  };
};

// Re-export navigation helper bound to react-router for page ports.
import { NAV_PAGE_TO_PATH } from "@/config/navigation.config.js";
export const useNav = () => {
  const navigate = useNavigate();
  return (page: string, param?: string | number) => {
    const path =
      (NAV_PAGE_TO_PATH as Record<string, string>)[page] ?? `/${page}`;
    navigate(param != null ? `${path}/${param}` : path);
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// ENTERPRISE DESIGN-SYSTEM COMPOSITIONS (2026-05)
// Standardise the patterns that previously varied page-to-page: page chrome,
// in-page section headings, list toolbars, and async data states. The
// canonical list page is:
//   PageContainer > PageHeader > Toolbar > DataState > Table > Pagination
// ═══════════════════════════════════════════════════════════════════════════

/**
 * PageContainer — consistent page padding + max content width. Wrap a page body.
 * size: "default" (1600) | "wide" (1760) | "narrow" (4xl, for forms).
 */
export const PageContainer: React.FC<{
  children?: React.ReactNode;
  className?: string;
  size?: "default" | "wide" | "narrow";
}> = ({ children, className = "", size = "default" }) => {
  const max =
    size === "wide"
      ? "max-w-[1760px]"
      : size === "narrow"
        ? "max-w-4xl"
        : "max-w-[1600px]";
  return (
    <div className={cn("w-full mx-auto px-4 py-5 sm:px-5 lg:px-6 lg:py-6", max, className)}>
      {children}
    </div>
  );
};

/** SectionTitle — consistent in-page heading: overline + title (+ optional action). */
export const SectionTitle: React.FC<{
  overline?: React.ReactNode;
  title: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}> = ({ overline, title, action, className = "" }) => (
  <div className={cn("flex items-end justify-between gap-3 mb-3", className)}>
    <div className="min-w-0">
      {overline && (
        <div className="text-2xs uppercase tracking-wider text-text-muted font-semibold mb-0.5">
          {overline}
        </div>
      )}
      <h2 className="text-md font-semibold tracking-tight text-text-strong">{title}</h2>
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

/**
 * Toolbar — standard list toolbar: optional search + filter slot + action slot.
 * One responsive layout for every index page (replaces the 5 bespoke filter
 * bars the audit found).
 *
 * Props:
 *   searchValue / onSearchChange(e) / onSearchSubmit() / searchPlaceholder
 *   filters : ReactNode  — Select / SegmentedControl / toggle chips
 *   actions : ReactNode  — primary + secondary Buttons (pushed right)
 */
export const Toolbar: React.FC<{
  searchValue?: string;
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchSubmit?: () => void;
  searchPlaceholder?: string;
  filters?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}> = ({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  searchPlaceholder = "Rechercher…",
  filters,
  actions,
  className = "",
}) => {
  const hasSearch = typeof onSearchChange === "function";
  return (
    <div className={cn("flex flex-col gap-2.5 mb-4 lg:flex-row lg:items-center", className)}>
      {hasSearch && (
        <div className="w-full lg:max-w-xs">
          <Input
            icon="search"
            value={searchValue ?? ""}
            onChange={onSearchChange}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter" && typeof onSearchSubmit === "function") onSearchSubmit();
            }}
            placeholder={searchPlaceholder}
          />
        </div>
      )}
      {filters && <div className="flex flex-wrap items-center gap-2">{filters}</div>}
      {actions && <div className="flex items-center gap-2 lg:ml-auto">{actions}</div>}
    </div>
  );
};

/**
 * DataState — standard async wrapper for list / detail bodies. Renders a
 * skeleton while loading, an error panel on failure, an EmptyState when there
 * are no rows, otherwise the children. Replaces the ad-hoc
 * `{loading ? <Spinner/> : rows.length===0 ? <div>Aucun…</div> : <table/>}`
 * ladders across pages.
 */
export const DataState: React.FC<{
  loading?: boolean;
  error?: string | Error | null;
  isEmpty?: boolean;
  onRetry?: () => void;
  skeleton?: "table" | "cards" | "form" | React.ReactElement;
  rows?: number;
  empty?: { icon?: string; title?: React.ReactNode; description?: React.ReactNode; action?: React.ReactNode };
  children?: React.ReactNode;
}> = ({ loading, error, isEmpty, onRetry, skeleton = "table", rows = 6, empty = {}, children }) => {
  if (loading) {
    if (React.isValidElement(skeleton)) return skeleton;
    if (skeleton === "cards") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: Math.min(rows, 6) }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-lg" />
          ))}
        </div>
      );
    }
    if (skeleton === "form") {
      return (
        <div className="space-y-4 max-w-2xl">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-9 w-full" />
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-border-subtle overflow-hidden">
        <Skeleton className="h-10 w-full rounded-none" />
        <div className="divide-y divide-border-subtle">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-3">
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <EmptyState
        icon="alert-triangle"
        title="Erreur de chargement"
        description={typeof error === "string" ? error : "Une erreur est survenue. Réessayez."}
        action={
          onRetry ? (
            <Button variant="secondary" size="sm" icon="refresh-cw" onClick={onRetry}>
              Réessayer
            </Button>
          ) : null
        }
      />
    );
  }
  if (isEmpty) {
    return (
      <EmptyState
        icon={empty.icon ?? "inbox"}
        title={empty.title ?? "Aucune donnée"}
        description={empty.description}
        action={empty.action}
      />
    );
  }
  return <>{children}</>;
};
